import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import StyledSelect from "./StyledSelect";

const formatMessageTime = (timestamp) => {
  const d = new Date(timestamp);
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ExportConversations = () => {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [bots, setBots] = useState([]);
  const [selectedBot, setSelectedBot] = useState("");
  const [periodFilter, setPeriodFilter] = useState("7days");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [exportFormat, setExportFormat] = useState("txt");
  const [migrating, setMigrating] = useState(false);
  const [migrateMessage, setMigrateMessage] = useState(null);

  const [conversations, setConversations] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationsError, setConversationsError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedConversationIds, setSelectedConversationIds] = useState(new Set());
  const [deletingConversations, setDeletingConversations] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState(null);
  const [blockingConversationIds, setBlockingConversationIds] = useState(new Set());
  const [blockIpMessage, setBlockIpMessage] = useState(null);
  const [blockedIps, setBlockedIps] = useState(new Set());

  useEffect(() => {
    axios
      .get(`${API_BASE}/api/clients`)
      .then((response) => setClients(response.data))
      .catch((err) => console.error("Erro ao buscar clientes:", err));
  }, []);

  useEffect(() => {
    if (!selectedClient) {
      setBots([]);
      setSelectedBot("");
      setBlockedIps(new Set());
      return;
    }
    setSelectedBot("");
    axios
      .get(`${API_BASE}/api/bots?clientId=${encodeURIComponent(selectedClient)}`)
      .then((response) => {
        setBots(Array.isArray(response.data) ? response.data : []);
      })
      .catch((err) => {
        console.error("Erro ao buscar bots:", err);
        setBots([]);
      });
  }, [selectedClient]);

  const refreshBlockedIps = useCallback(async (clientIdOverride) => {
    const clientIdToUse = clientIdOverride || selectedClient;
    if (!clientIdToUse) {
      setBlockedIps(new Set());
      return;
    }
    try {
      const res = await axios.get(
        `${API_BASE}/api/clients/${encodeURIComponent(clientIdToUse)}/blocked-ips`
      );
      const normalized = Array.isArray(res.data?.blockedIps)
        ? res.data.blockedIps.map((ip) => String(ip || "").trim()).filter(Boolean)
        : [];
      setBlockedIps(new Set(normalized));
    } catch (err) {
      console.error("Erro ao carregar IPs bloqueados:", err);
      setBlockedIps(new Set());
    }
  }, [selectedClient]);

  useEffect(() => {
    refreshBlockedIps(selectedClient);
  }, [selectedClient, refreshBlockedIps]);

  const loadConversations = async () => {
    if (!selectedClient) {
      setConversationsError("Selecione um cliente.");
      return;
    }
    setConversationsError(null);
    setLoadingConversations(true);
    const params = new URLSearchParams({ clientId: selectedClient });
    if (selectedBot) {
      params.set("botId", selectedBot);
    }
    if (periodFilter === "7days" || periodFilter === "30days") {
      params.set("range", periodFilter);
    } else if (periodFilter === "custom") {
      if (!startDate || !endDate) {
        setLoadingConversations(false);
        setConversationsError("Selecione data inicial e data final.");
        return;
      }
      if (new Date(`${startDate}T00:00:00`) > new Date(`${endDate}T00:00:00`)) {
        setLoadingConversations(false);
        setConversationsError("A data inicial não pode ser maior que a data final.");
        return;
      }
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    }
    try {
      const res = await axios.get(`${API_BASE}/api/conversations?${params}`);
      setConversations(res.data.conversations || []);
      setExpandedId(null);
      setSelectedConversationIds(new Set());
      await refreshBlockedIps(selectedClient);
    } catch (err) {
      setConversations([]);
      setSelectedConversationIds(new Set());
      setConversationsError(
        err.response?.data?.error || "Erro ao carregar conversas. Verifique se a API está rodando."
      );
    } finally {
      setLoadingConversations(false);
    }
  };

  const handleSelectConversation = (conversationId, checked) => {
    setSelectedConversationIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(conversationId);
      } else {
        next.delete(conversationId);
      }
      return next;
    });
  };

  const handleSelectAllVisible = () => {
    const visibleIds = conversations.map((conv) => conv._id);
    setSelectedConversationIds(new Set(visibleIds));
  };

  const handleClearSelection = () => {
    setSelectedConversationIds(new Set());
  };

  const handleDeleteSelected = async () => {
    if (!selectedClient || selectedConversationIds.size === 0) {
      return;
    }
    const selectedCount = selectedConversationIds.size;
    const confirmed = window.confirm(
      `Tem certeza que deseja excluir ${selectedCount} conversa${selectedCount !== 1 ? "s" : ""}? Essa ação não pode ser desfeita.`
    );
    if (!confirmed) {
      return;
    }

    setDeleteMessage(null);
    setDeletingConversations(true);
    const payload = {
      clientId: selectedClient,
      conversationIds: Array.from(selectedConversationIds),
    };
    if (selectedBot) {
      payload.botId = selectedBot;
    }

    try {
      const res = await axios.post(`${API_BASE}/api/conversations/bulk-delete`, payload);
      const deletedCount = res.data?.deletedCount || 0;
      const skippedCount = res.data?.notFoundOrUnauthorized || 0;
      setDeleteMessage({
        type: "success",
        text:
          skippedCount > 0
            ? `${deletedCount} conversa(s) excluída(s). ${skippedCount} não foram encontradas ou não pertencem ao escopo selecionado.`
            : `${deletedCount} conversa(s) excluída(s) com sucesso.`,
      });
      setSelectedConversationIds(new Set());
      if (expandedId && payload.conversationIds.includes(expandedId)) {
        setExpandedId(null);
      }
      await loadConversations();
    } catch (err) {
      setDeleteMessage({
        type: "error",
        text: err.response?.data?.error || "Erro ao excluir conversas. Tente novamente.",
      });
    } finally {
      setDeletingConversations(false);
    }
  };

  const handleBlockIp = async (conversation) => {
    const convId = conversation?._id;
    const ip = String(conversation?.clientIp || "").trim();
    if (!selectedClient || !convId || !ip) {
      return;
    }
    const confirmed = window.confirm(`Deseja bloquear o IP ${ip} para este cliente?`);
    if (!confirmed) return;

    setBlockIpMessage(null);
    setBlockingConversationIds((prev) => {
      const next = new Set(prev);
      next.add(convId);
      return next;
    });
    try {
      await axios.post(`${API_BASE}/api/clients/${encodeURIComponent(selectedClient)}/blocked-ips`, { ip });
      setBlockIpMessage({ type: "success", text: `IP ${ip} bloqueado com sucesso.` });
      await refreshBlockedIps(selectedClient);
    } catch (err) {
      setBlockIpMessage({
        type: "error",
        text: err.response?.data?.error || "Erro ao bloquear IP. Tente novamente.",
      });
    } finally {
      setBlockingConversationIds((prev) => {
        const next = new Set(prev);
        next.delete(convId);
        return next;
      });
    }
  };

  const handleExport = () => {
    if (!selectedClient) {
      alert("Por favor, selecione um cliente.");
      return;
    }
    let url = `${API_BASE}/api/export-history?clientId=${selectedClient}&format=${exportFormat}`;
    if (selectedBot) {
      url += `&botId=${encodeURIComponent(selectedBot)}`;
    }
    if (periodFilter === "7days" || periodFilter === "30days") {
      url += `&range=${periodFilter}`;
    } else {
      if (!startDate || !endDate) {
        alert("Selecione data inicial e data final para exportar.");
        return;
      }
      if (new Date(`${startDate}T00:00:00`) > new Date(`${endDate}T00:00:00`)) {
        alert("A data inicial não pode ser maior que a data final.");
        return;
      }
      url += `&startDate=${startDate}&endDate=${endDate}`;
    }
    axios
      .get(url, { responseType: "blob" })
      .then((response) => {
        const blob = new Blob([response.data], {
          type: exportFormat === "json" ? "application/json" : "text/plain",
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `historico_${selectedClient}.${exportFormat}`;
        link.click();
      })
      .catch((err) => {
        console.error("Erro ao exportar histórico:", err);
        alert("Erro ao exportar histórico. Verifique se a API está rodando.");
      });
  };

  const handleMigrateTemp = () => {
    setMigrateMessage(null);
    setMigrating(true);
    axios
      .post(`${API_BASE}/api/check-inactive-conversations?moveAll=true`)
      .then(() => {
        setMigrateMessage({
          type: "success",
          text: "Histórico temporário migrado. Atualize a lista de conversas se precisar.",
        });
      })
      .catch((err) => {
        setMigrateMessage({
          type: "error",
          text: err.response?.data?.error || "Erro ao migrar. Verifique se a API está rodando.",
        });
      })
      .finally(() => setMigrating(false));
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>💬 Conversas</h2>

      <div style={styles.form}>
        <div style={styles.filtersRow}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Cliente</label>
            <StyledSelect
              value={selectedClient}
              onChange={(v) => {
                setSelectedClient(v);
                setConversations([]);
                setConversationsError(null);
                setSelectedConversationIds(new Set());
                setDeleteMessage(null);
                setBlockIpMessage(null);
                setExpandedId(null);
              }}
              options={[
                { value: "", label: "— Escolha um cliente —" },
                ...clients.map((c) => ({ value: c.id, label: c.name })),
              ]}
              placeholder="— Escolha um cliente —"
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Bot (opcional)</label>
            <StyledSelect
              value={selectedBot}
              onChange={(v) => {
                setSelectedBot(v);
                setSelectedConversationIds(new Set());
                setDeleteMessage(null);
                setBlockIpMessage(null);
                setExpandedId(null);
              }}
              options={[
                { value: "", label: "— Todos os bots —" },
                ...bots.map((b) => ({ value: b.id, label: b.name || b.id })),
              ]}
              placeholder="— Todos os bots —"
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Período</label>
            <StyledSelect
              value={periodFilter}
              onChange={(v) => setPeriodFilter(v)}
              options={[
                { value: "7days", label: "Últimos 7 dias" },
                { value: "30days", label: "Últimos 30 dias" },
                { value: "custom", label: "Filtrar por data" },
              ]}
              placeholder="Período"
            />
          </div>
          {periodFilter === "custom" && (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Data inicial</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Data final</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={styles.dateInput}
                />
              </div>
            </>
          )}
          <div style={styles.buttonWrap}>
            <button
              type="button"
              onClick={loadConversations}
              disabled={loadingConversations || !selectedClient}
              style={styles.buttonPrimary}
            >
              {loadingConversations ? "Carregando…" : "Ver conversas"}
            </button>
          </div>
        </div>

        {conversationsError && (
          <p style={styles.messageError}>{conversationsError}</p>
        )}

        {conversations.length > 0 && (
          <div style={styles.conversationsList}>
            <p style={styles.listHeader}>
              {conversations.length} conversa{conversations.length !== 1 ? "s" : ""} encontrada
              {conversations.length !== 1 ? "s" : ""}.
            </p>
            <div style={styles.bulkActionsRow}>
              <span style={styles.bulkActionsInfo}>
                {selectedConversationIds.size} selecionada
                {selectedConversationIds.size !== 1 ? "s" : ""}
              </span>
              <div style={styles.bulkActionsButtons}>
                <button
                  type="button"
                  onClick={handleSelectAllVisible}
                  disabled={conversations.length === 0 || deletingConversations}
                  style={{ ...styles.buttonSmall, ...styles.buttonSecondaryAction }}
                >
                  Selecionar todas
                </button>
                <button
                  type="button"
                  onClick={handleClearSelection}
                  disabled={selectedConversationIds.size === 0 || deletingConversations}
                  style={{ ...styles.buttonSmall, ...styles.buttonSecondaryAction }}
                >
                  Limpar seleção
                </button>
                <button
                  type="button"
                  onClick={handleDeleteSelected}
                  disabled={selectedConversationIds.size === 0 || deletingConversations}
                  style={{ ...styles.buttonSmall, ...styles.buttonDanger }}
                >
                  {deletingConversations ? "Excluindo…" : "Excluir selecionadas"}
                </button>
              </div>
            </div>
            {deleteMessage && (
              <p style={deleteMessage.type === "success" ? styles.messageSuccess : styles.messageError}>
                {deleteMessage.text}
              </p>
            )}
            {blockIpMessage && (
              <p style={blockIpMessage.type === "success" ? styles.messageSuccess : styles.messageError}>
                {blockIpMessage.text}
              </p>
            )}
            {conversations.map((conv) => {
              const isExpanded = expandedId === conv._id;
              const isSelected = selectedConversationIds.has(conv._id);
              const isBlockingIp = blockingConversationIds.has(conv._id);
              const isIpBlocked = !!conv.clientIp && blockedIps.has(String(conv.clientIp).trim());
              const lastMsg = conv.messages?.[conv.messages.length - 1];
              const previewDate = lastMsg?.timestamp || conv.updatedAt || conv.createdAt;
              return (
                <div
                  key={conv._id}
                  style={{
                    ...styles.conversationCard,
                    ...(isSelected ? styles.conversationCardSelected : {}),
                  }}
                >
                  <div style={styles.conversationHeader}>
                    <label
                      style={styles.selectConversationWrap}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => handleSelectConversation(conv._id, e.target.checked)}
                      />
                    </label>
                    <button
                      type="button"
                      style={styles.conversationHeaderButton}
                      onClick={() => setExpandedId(isExpanded ? null : conv._id)}
                    >
                    <span style={styles.convMeta}>
                      <span style={styles.convUserId}>{conv.displayName || conv.userId}</span>
                      <span style={styles.convCount}>
                        {conv.messages?.length || 0} mensagens
                      </span>
                      {conv.clientIp && (
                        <>
                          <span style={styles.convIp}>IP: {conv.clientIp}</span>
                          {isIpBlocked && (
                            <span style={styles.blockedBadge}>IP bloqueado</span>
                          )}
                          <span
                            style={{
                              ...styles.blockIpButton,
                              ...((isBlockingIp || isIpBlocked) ? styles.blockIpButtonDisabled : {}),
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              if (isBlockingIp || isIpBlocked) return;
                              handleBlockIp(conv);
                            }}
                          >
                            {isIpBlocked ? "Já bloqueado" : isBlockingIp ? "Bloqueando..." : "Bloquear este IP"}
                          </span>
                        </>
                      )}
                      <span style={styles.convDate}>
                        {formatMessageTime(previewDate)}
                      </span>
                    </span>
                      <span style={styles.expandIcon}>{isExpanded ? "▼" : "▶"}</span>
                    </button>
                  </div>
                  {isExpanded && conv.messages?.length > 0 && (
                    <div style={styles.messagesBox}>
                      {conv.messages.map((msg, idx) => (
                        <div
                          key={idx}
                          style={{
                            ...styles.messageBubble,
                            ...(msg.role === "user"
                              ? styles.messageUser
                              : styles.messageBot),
                          }}
                        >
                          <span style={styles.messageRole}>
                            {msg.role === "user" ? "Usuário" : "Bot"}
                          </span>
                          <span style={styles.messageTime}>
                            {formatMessageTime(msg.timestamp)}
                          </span>
                          <div style={styles.messageContent}>{msg.content}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {!loadingConversations && conversations.length === 0 && selectedClient && !conversationsError && (
          <p style={styles.hint}>Selecione cliente e período e clique em &quot;Ver conversas&quot;.</p>
        )}

        <div style={styles.exportSection}>
          <p style={styles.sectionLabel}>Baixar histórico</p>
          <div style={styles.exportRow}>
            <div style={styles.formatSelectWrap}>
              <StyledSelect
                value={exportFormat}
                onChange={setExportFormat}
                options={[
                  { value: "txt", label: "TXT" },
                  { value: "json", label: "JSON" },
                ]}
                placeholder="Formato"
              />
            </div>
            <button onClick={handleExport} style={styles.button}>
              ⬇️ Baixar histórico
            </button>
          </div>
        </div>

        <div style={styles.migrateSection}>
          <p style={styles.migrateHint}>
            Se não aparecer conversas, elas podem estar só no temporário. Migre
            para o permanente e clique em &quot;Ver conversas&quot; de novo.
          </p>
          <button
            type="button"
            onClick={handleMigrateTemp}
            disabled={migrating}
            style={{ ...styles.button, ...styles.buttonSecondary }}
          >
            {migrating ? "⏳ Migrando…" : "📥 Migrar histórico temporário agora"}
          </button>
          {migrateMessage && (
            <p
              style={
                migrateMessage.type === "success"
                  ? styles.messageSuccess
                  : styles.messageError
              }
            >
              {migrateMessage.text}
            </p>
          )}
        </div>

        {/* Vídeo de ajuda - Conversas */}
        <section style={{ marginTop: 48, paddingTop: 24, borderTop: "1px solid rgba(0, 212, 255, 0.2)" }}>
          <h3 style={{ color: "#00D4FF", fontSize: "1rem", fontWeight: 600, marginBottom: 16, textTransform: "uppercase", letterSpacing: 1 }}>Vídeo de ajuda</h3>
          <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden", maxWidth: 800, margin: "0 auto", borderRadius: 12, border: "1px solid rgba(0, 212, 255, 0.3)" }}>
            <iframe
              title="Como usar Conversas"
              src="https://www.loom.com/embed/184babb4853a4aaf8246dd22c3264e16"
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
              frameBorder="0"
              allow="fullscreen"
              allowFullScreen
            />
          </div>
        </section>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: "24px 20px",
    maxWidth: "900px",
    margin: "0 auto",
  },
  title: {
    fontSize: "2rem",
    fontWeight: "700",
    textAlign: "center",
    marginBottom: "24px",
    background: "linear-gradient(135deg, #B026FF 0%, #00D4FF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    textTransform: "uppercase",
    letterSpacing: "2px",
  },
  form: {
    background: "linear-gradient(135deg, rgba(10, 14, 39, 0.95) 0%, rgba(176, 38, 255, 0.25) 100%)",
    padding: "28px 32px",
    borderRadius: "16px",
    border: "1px solid rgba(0, 212, 255, 0.3)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.4)",
  },
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    alignItems: "flex-end",
    marginBottom: "20px",
  },
  inputGroup: {
    flex: "1 1 140px",
    minWidth: "140px",
  },
  label: {
    display: "block",
    marginBottom: "6px",
    color: "#B0BEC5",
    fontWeight: "600",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  dateInput: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid rgba(0, 212, 255, 0.3)",
    fontSize: "14px",
    background: "rgba(10, 14, 39, 0.8)",
    color: "#fff",
  },
  buttonWrap: {
    flex: "0 0 auto",
  },
  buttonPrimary: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #B026FF 0%, #0066FF 100%)",
    color: "#fff",
    border: "1px solid rgba(0, 212, 255, 0.5)",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "700",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  hint: {
    color: "#94a3b8",
    fontSize: "14px",
    marginBottom: "20px",
  },
  conversationsList: {
    marginBottom: "28px",
  },
  listHeader: {
    color: "#B0BEC5",
    fontSize: "13px",
    fontWeight: "600",
    marginBottom: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  bulkActionsRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: "12px",
  },
  bulkActionsInfo: {
    color: "#94a3b8",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  bulkActionsButtons: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },
  buttonSmall: {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid rgba(0, 212, 255, 0.35)",
    background: "rgba(10, 14, 39, 0.8)",
    color: "#e2e8f0",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  },
  buttonSecondaryAction: {
    borderColor: "rgba(0, 212, 255, 0.35)",
  },
  buttonDanger: {
    borderColor: "rgba(248, 113, 113, 0.6)",
    background: "rgba(127, 29, 29, 0.5)",
    color: "#fecaca",
  },
  conversationCard: {
    marginBottom: "10px",
    borderRadius: "12px",
    border: "1px solid rgba(0, 212, 255, 0.2)",
    overflow: "hidden",
    background: "rgba(5, 7, 16, 0.5)",
  },
  conversationCardSelected: {
    border: "1px solid rgba(0, 212, 255, 0.6)",
    boxShadow: "0 0 0 1px rgba(0, 212, 255, 0.2)",
  },
  conversationHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    paddingLeft: "12px",
    paddingRight: "8px",
  },
  selectConversationWrap: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "24px",
  },
  conversationHeaderButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 18px",
    background: "transparent",
    border: "none",
    color: "#e2e8f0",
    cursor: "pointer",
    textAlign: "left",
    fontSize: "14px",
  },
  convMeta: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "12px",
  },
  convUserId: {
    fontFamily: "monospace",
    fontSize: "13px",
    color: "#94a3b8",
  },
  convCount: {
    color: "#00D4FF",
    fontSize: "13px",
  },
  convDate: {
    color: "#64748b",
    fontSize: "12px",
  },
  convIp: {
    color: "#fda4af",
    fontSize: "12px",
    fontFamily: "monospace",
  },
  blockIpButton: {
    padding: "4px 8px",
    borderRadius: "6px",
    border: "1px solid rgba(248, 113, 113, 0.55)",
    background: "rgba(127, 29, 29, 0.45)",
    color: "#fecaca",
    fontSize: "11px",
    fontWeight: "600",
    cursor: "pointer",
  },
  blockIpButtonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },
  blockedBadge: {
    padding: "4px 8px",
    borderRadius: "999px",
    border: "1px solid rgba(251, 191, 36, 0.6)",
    background: "rgba(120, 53, 15, 0.45)",
    color: "#fcd34d",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  expandIcon: {
    color: "#00D4FF",
    fontSize: "10px",
  },
  messagesBox: {
    padding: "12px 18px 18px",
    borderTop: "1px solid rgba(0, 212, 255, 0.2)",
    maxHeight: "400px",
    overflowY: "auto",
    background: "rgba(51, 65, 85, 0.5)",
  },
  messageBubble: {
    marginBottom: "12px",
    padding: "12px 14px",
    borderRadius: "12px",
    fontSize: "14px",
    lineHeight: 1.5,
  },
  messageUser: {
    background: "rgba(80, 112, 255, 0.28)",
    borderLeft: "3px solid #5070FF",
    marginLeft: "0",
    marginRight: "24px",
  },
  messageBot: {
    background: "rgba(176, 38, 255, 0.15)",
    borderLeft: "3px solid #B026FF",
    marginLeft: "24px",
    marginRight: "0",
  },
  messageRole: {
    display: "block",
    fontSize: "11px",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#94a3b8",
    marginBottom: "4px",
  },
  messageTime: {
    display: "block",
    fontSize: "11px",
    color: "#64748b",
    marginBottom: "6px",
  },
  messageContent: {
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  exportSection: {
    marginTop: "24px",
    paddingTop: "20px",
    borderTop: "1px solid rgba(0, 212, 255, 0.2)",
  },
  formatSelectWrap: {
    width: "120px",
    minWidth: "120px",
  },
  sectionLabel: {
    color: "#B0BEC5",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    marginBottom: "10px",
  },
  exportRow: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  button: {
    padding: "12px 20px",
    background: "linear-gradient(135deg, #B026FF 0%, #0066FF 100%)",
    color: "#fff",
    border: "1px solid #00D4FF",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
  },
  migrateSection: {
    marginTop: "20px",
    paddingTop: "20px",
    borderTop: "1px solid rgba(0, 212, 255, 0.2)",
  },
  migrateHint: {
    color: "#94a3b8",
    fontSize: "13px",
    marginBottom: "12px",
    lineHeight: 1.4,
  },
  buttonSecondary: {
    background: "linear-gradient(135deg, rgba(0, 212, 255, 0.25) 0%, rgba(176, 38, 255, 0.25) 100%)",
    borderColor: "rgba(0, 212, 255, 0.5)",
  },
  messageSuccess: {
    color: "#22c55e",
    fontSize: "14px",
    marginTop: "10px",
    fontWeight: "600",
  },
  messageError: {
    color: "#f87171",
    fontSize: "14px",
    marginTop: "8px",
    fontWeight: "500",
  },
};

export default ExportConversations;
